/* eslint-disable @typescript-eslint/no-extraneous-class -- needed*/

import { reverseRecord } from '@unifyapps/carbon/no-code/utils/interfaceRecordUtils';
import { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import {
  ApplicationType,
  type EntityDetails,
  IntegrationApps,
  type InterfaceEntity,
  type InterfacePathNode,
  InterfaceType,
  SecurityType,
} from '@unifyapps/defs/types/interface';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import { InterfacePageEntityComponentType } from '@unifyapps/defs/types/page';
import { fallbackLng } from '@unifyapps/i18n/settings';
import { getLocaleBasedOnLanguage } from '@unifyapps/i18n/utils/getLocaleBasedOnLanguage';
import { Op } from '@unifyapps/network/generated/models/op';
import type { Query } from '@unifyapps/network/generated/models/query';
import _compact from 'lodash/compact';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../consts/empty';
import {
  AI_BUILDING_PAGE_SLUG,
  COMMON_NAMESPACE_ID_PREFIX,
  FIGMA_BUILDING_PAGE_SLUG,
  GLOBAL_INTERFACE_PAGE_ID_PREFIX,
  GLOBAL_INTERFACE_PAGE_SLUG_PREFIX,
  NO_INTERFACE_RECORD_ID,
} from '../../../const';
import type { InterfaceComponent } from '../../../types/interfaceComponent';
import {
  getAllInterfacePageFullRawPaths,
  getBuilderValueByParamNameForPageId,
  getFullRawNonNormalizedPathByPageId,
  type ResolveResult,
  resolvePageFromPathForPathAndSlug,
} from '../../../utils/path/interfacePathMatcher';
import {
  getNormalizedPathFromSlugOrPathNames,
  getPageSlugAndPath,
} from '../../../utils/path/interfacePathNormalize';
import { InterfaceModes } from '../types';

interface GetApplicationLocaleParams {
  interfaceRecord?: InterfaceEntity;
  userLocale?: string;
  mode?: InterfaceModes;
  localeFromCookie?: string;
}

type AppHostingType = 'DOMAIN_BASED' | 'PATH_BASED';

// This syncEntites no need to store in the database, just fetch them from the network
// and store them in the mmkv
const SYNC_ENTITIES_TO_FILTER = new Set<UAEntityTypes>([UAEntityTypes.CustomIcon]);

class InterfaceHelper {
  static commonNamespaceComponentId = 'common';

  static getInterfaceType = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.type || InterfaceType.Application;
  };

  static isApplicationType = (interfaceRecord: InterfaceEntity | undefined) => {
    return (
      !interfaceRecord?.properties.type ||
      interfaceRecord?.properties.type === InterfaceType.Application
    );
  };

  static isPagePublishableAsTemplate = (
    interfaceRecord: InterfaceEntity | undefined,
    componentType: InterfacePageEntityComponentType | undefined,
  ): boolean => {
    const applicationType = interfaceRecord?.properties.manifest?.type;
    return (
      componentType === InterfacePageEntityComponentType.PAGE &&
      InterfaceHelper.isApplicationType(interfaceRecord) &&
      !InterfaceHelper.isTemplateInterface(interfaceRecord) &&
      !InterfaceHelper.isNoInterface(interfaceRecord) &&
      (applicationType === ApplicationType.WEB || applicationType === ApplicationType.MOBILE)
    );
  };

  static isTemplateComponentType = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.type === InterfaceType.TemplateComponent;
  };

  /**
   * Identifies builders running without a real interface record (page-template,
   * field, form, walkthrough preview, etc.). These surfaces use NO_INTERFACE_RECORD_ID
   * as a sentinel — feature gates downstream (app config, save chain parent
   * entity wiring, collab subscriptions, settings panels) should branch off
   * this single signal rather than re-deriving it via inline id comparisons.
   */
  static isNoInterface = (interfaceRecord: InterfaceEntity | undefined): boolean => {
    return interfaceRecord?.id === NO_INTERFACE_RECORD_ID;
  };

  static isTemplateInterface = (interfaceRecord: InterfaceEntity | undefined): boolean => {
    return interfaceRecord?.properties.isTemplate ?? false;
  };

  static getInterfaceBaseDevice = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.deviceDetails?.base ?? DeviceVariantType.DESKTOP;
  };

  static getInterfacePlatform = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.manifest?.type || ApplicationType.WEB;
  };

  static getInterfaceSecurityDetails = (
    interfaceRecord?: Partial<InterfaceEntity>,
    parentInterfaceRecord?: Partial<InterfaceEntity>,
  ) => {
    // A packaged app inherits its security (CSP + public/private type) from the package.
    // The backend merges that onto properties.package.security only on /auth/interface/deployed
    // (populatePackagedApplicationDetails), so the matrix runtime and deployed-record callers hit
    // package.security first. The platform builder reads the raw findEntityById entity, which has
    // no such merge — there package.security is absent, so we fall back to the separately fetched
    // parent's own security.
    if (interfaceRecord?.properties?.parentInterfaceId) {
      return (
        interfaceRecord.properties.package?.security ??
        parentInterfaceRecord?.properties?.security ??
        interfaceRecord.properties.security
      );
    }
    return interfaceRecord?.properties?.package?.security ?? interfaceRecord?.properties?.security;
  };

  static isPublicInterface = (interfaceRecord: InterfaceEntity | undefined) => {
    return this.getInterfaceSecurityDetails(interfaceRecord)?.type === 'PUBLIC';
  };

  static isAppAnalyticsEnabled = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.appAnalytics?.enabled ?? false;
  };

  static getOzonetelConfig = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.Ozonetel];
  };

  static isOzonetelEnabled = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.Ozonetel]?.enabled ?? false;
  };

  static getPusherConfig = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.Pusher];
  };

  static isPusherEnabled = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.Pusher]?.enabled ?? false;
  };

  static getMicrosoftClarityConfig = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.MicrosoftClarity];
  };

  static isMicrosoftClarityEnabled = (interfaceRecord: InterfaceEntity | undefined) => {
    return (
      interfaceRecord?.properties.integrations?.[IntegrationApps.MicrosoftClarity]?.enabled ?? false
    );
  };

  static getUAEPassConfig = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.UAEPass];
  };

  static isUAEPassEnabled = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.UAEPass]?.enabled ?? false;
  };

  static getRazorpayConfig = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.Razorpay];
  };

  static isRazorpayEnabled = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.Razorpay]?.enabled ?? false;
  };

  static getCleverTapConfig = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.CleverTap];
  };

  static isCleverTapEnabled = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.CleverTap]?.enabled ?? false;
  };

  static getYellowAIConfig = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.YellowAI];
  };

  static isYellowAIEnabled = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.integrations?.[IntegrationApps.YellowAI]?.enabled ?? false;
  };

  /** returns a Set of IntegrationApps whose `.enabled` flag is true on the interface record */
  static getEnabledIntegrationApps = (
    interfaceRecord: InterfaceEntity | undefined,
  ): Set<IntegrationApps> => {
    const integrations = interfaceRecord?.properties.integrations;
    if (!integrations) return new Set<IntegrationApps>();

    const enabled = new Set<IntegrationApps>();
    for (const key of Object.values(IntegrationApps)) {
      if ((integrations as Record<string, { enabled?: boolean } | undefined>)[key]?.enabled) {
        enabled.add(key);
      }
    }
    return enabled;
  };

  static isAppVariantsEnabled = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.appVariants?.enabled ?? false;
  };

  static isTemplateComponentPage = (pageType?: InterfacePageEntityComponentType) => {
    return pageType === InterfacePageEntityComponentType.TEMPLATE_COMPONENT;
  };

  static getDefaultPageId = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.navigation?.defaultPageId;
  };

  static isWalkthroughEnabled = (interfaceRecord: InterfaceEntity | undefined) => {
    return (
      this.getInterfacePlatform(interfaceRecord) === ApplicationType.WEB &&
      interfaceRecord?.properties.type === InterfaceType.Application
    );
  };

  static getDefaultAppVariant = (interfaceRecord: InterfaceEntity | undefined) => {
    return InterfaceHelper.getAppVariants(interfaceRecord)?.variants.find(
      (variant) => variant.isDefault,
    );
  };

  static getDefaultLocale = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.locale?.defaultLocale;
  };

  static getPageIds = (interfaceRecord: InterfaceEntity | undefined) => {
    return Object.keys(this.getInterfacePageIdVsSlugMap(interfaceRecord)).filter((pageId) =>
      Boolean(pageId),
    );
  };

  static getPageSlugFromId = (interfaceRecord: InterfaceEntity | undefined, pageId: string) => {
    return this.getInterfacePageIdVsSlugMap(interfaceRecord)[pageId];
  };

  static getLocale = (params: GetApplicationLocaleParams): string => {
    const { userLocale, interfaceRecord, mode = InterfaceModes.RUNNER, localeFromCookie } = params;
    const defaultLocale = this.getDefaultLocale(interfaceRecord);
    const browserLocale =
      typeof navigator !== 'undefined' && navigator.language ? navigator.language : undefined;
    const allLocales = this.getAllLocalesSet(interfaceRecord);

    const hasLocaleFromCookie = localeFromCookie !== undefined && allLocales.has(localeFromCookie);
    const hasUserLocale = userLocale !== undefined && allLocales.has(userLocale);
    const hasBrowserLocale = Boolean(browserLocale && allLocales.has(browserLocale));

    if (hasLocaleFromCookie) {
      return localeFromCookie;
    } else if (mode === InterfaceModes.BUILDER && defaultLocale !== undefined) {
      return defaultLocale;
    } else if (hasUserLocale) {
      return userLocale;
    } else if (hasBrowserLocale) {
      return browserLocale || fallbackLng;
    }

    // if exact locale is not available, try to find a default locale for the language
    // example: userLocale is 'en-IN', but 'en-US' is not available, so try to find 'en'
    const expectedUserLocale = localeFromCookie ?? userLocale ?? browserLocale;

    if (expectedUserLocale) {
      // get default locale for the language
      const defaultLocaleAvailable = getLocaleBasedOnLanguage(expectedUserLocale, allLocales);
      // if default locale is available, return it
      if (defaultLocaleAvailable) {
        return defaultLocaleAvailable;
      }
    }

    // If nothing matches, then return the default locale set in the interface. If not set, then return the fallback locale.
    return defaultLocale || fallbackLng;
  };

  static getBuilderLocale = (userLocale?: string, interfaceRecord?: InterfaceEntity) => {
    const defaultLocale = this.getDefaultLocale(interfaceRecord);
    return defaultLocale ?? userLocale ?? fallbackLng;
  };

  static getSupportedLocales = (interfaceRecord: InterfaceEntity | undefined) => {
    return (interfaceRecord?.properties.locale?.supportedLocales || EMPTY_ARRAY) as string[];
  };

  static getAllLocalesSet = (interfaceRecord: InterfaceEntity | undefined) => {
    const interfaceLocales = new Set<string>();
    const locale = interfaceRecord?.properties.locale;

    if (!locale) {
      return interfaceLocales;
    }

    interfaceLocales.add(locale.defaultLocale);

    this.getSupportedLocales(interfaceRecord).forEach((supportedLocale) => {
      interfaceLocales.add(supportedLocale);
    });

    return interfaceLocales;
  };

  // this will return a map of language to default locale
  // for example: { en: 'en-US', fr: 'fr-FR' }
  static getLanguageToDefaultLocaleMap = (interfaceRecord: InterfaceEntity | undefined) => {
    const languageToDefaultLocaleMap = new Map<string, string>();
    const interfaceLocales = this.getAllLocalesSet(interfaceRecord);

    interfaceLocales.forEach((locale) => {
      const language = locale.split('-')[0];

      if (!languageToDefaultLocaleMap.has(language)) {
        languageToDefaultLocaleMap.set(language, locale);
      }
    });

    return languageToDefaultLocaleMap;
  };

  static isAIBuilding = (interfaceComponent: InterfaceComponent) => {
    return (
      interfaceComponent.type === InterfacePageEntityComponentType.PAGE &&
      (interfaceComponent.slug === AI_BUILDING_PAGE_SLUG ||
        interfaceComponent.slug === FIGMA_BUILDING_PAGE_SLUG)
    );
  };

  /**
   * @param interfaceComponent - the component for which we want to get the componentId. it can be of type page, module, field, form or walkthrough
   * @returns the componentId for the given component. if the component is of type page, it will return the pageId, if it is of type module, field, form or walkthrough,
   * it will return the id of the component
   */
  static getInterfaceComponentIdFromSlug = (
    interfaceRecord: InterfaceEntity | undefined,
    interfaceComponent: InterfaceComponent,
  ) => {
    switch (interfaceComponent.type) {
      case InterfacePageEntityComponentType.WALKTHROUGH:
      case InterfacePageEntityComponentType.MODULE:
      case InterfacePageEntityComponentType.FIELD:
      case InterfacePageEntityComponentType.FORM:
      case InterfacePageEntityComponentType.TEMPLATE_COMPONENT: {
        return interfaceComponent.id;
      }
      case InterfacePageEntityComponentType.PAGE: {
        // if path is present, that means we can use new business logic to determine the interface component
        if (interfaceComponent.path) {
          const pageIdFromPath = InterfaceHelper.resolvePageIdFromPath(
            interfaceComponent.path,
            interfaceComponent.slug,
            interfaceRecord,
          );

          // Trust the id when the caller already resolved it (e.g. BasePageLayout sets it
          // from pageRecord.id after the SSR pipeline's slug-query fallback). Re-resolving
          // from path/slug would return undefined for unmapped slugs and let
          // buildInterfacePageEmbeddedRequest short-circuit to defaultPageId, which renders
          // the home page on top of a non-home URL.
          return pageIdFromPath ?? interfaceComponent.id;
        }

        const interfaceSlugVsPageIdMap =
          InterfaceHelper.getInterfaceSlugVsPageIdMap(interfaceRecord);
        const pageIdFromSlug = interfaceSlugVsPageIdMap[interfaceComponent.slug];

        return pageIdFromSlug ?? interfaceComponent.id;
      }
    }
  };

  static getInterfaceComponentIdIfSlugIsId = (
    interfaceRecord: InterfaceEntity | undefined,
    interfaceComponent: InterfaceComponent,
  ) => {
    if (interfaceComponent.type === InterfacePageEntityComponentType.PAGE) {
      const interfacePageIdVsSlugMap = InterfaceHelper.getInterfacePageIdVsSlugMap(interfaceRecord);
      const isSlugId = interfaceComponent.slug in interfacePageIdVsSlugMap;

      // handling for module type APP where the slug is the default page id
      // in module the default page id is always string
      const isSlugIsDefaultPageId =
        interfaceComponent.slug === InterfaceHelper.getDefaultPageId(interfaceRecord);

      if (isSlugId || isSlugIsDefaultPageId) {
        return interfaceComponent.slug;
      }
    }
  };

  static getFlags(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return interfaceRecord?.properties?.flags;
  }

  static getTheme(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return interfaceRecord?.properties?.theme;
  }

  static getThemeOutput(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return InterfaceHelper.getTheme(interfaceRecord)?.output;
  }

  static getDefaultThemeId(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return InterfaceHelper.getTheme(interfaceRecord)?.defaultThemeId;
  }

  // single accessor so the backend can move the signal (properties -> entity envelope) without touching callers
  static isInterfaceOverridden(record: InterfaceEntity | undefined): boolean {
    return Boolean(record?.properties.isOverridden);
  }

  // one builder used by BOTH the server prefetch and the client hook, so their query keys always match
  static getInterfaceOverrideQueryFilters(interfaceId: string): Query {
    return {
      filter: {
        op: Op.AND,
        values: [{ field: 'properties.interfaceId', op: Op.EQUAL, values: [interfaceId] }],
      },
      page: { limit: 1, offset: 0 },
    };
  }

  static customCode(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return interfaceRecord?.properties?.customCode;
  }

  static htmlAttributes(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return interfaceRecord?.properties?.customCode?.htmlAttributes;
  }

  static isOfflineModeEnabled(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return Boolean(interfaceRecord?.properties?.offlineMode?.enabled);
  }

  static isOfflineSyncableForEntity(
    interfaceRecord: Partial<InterfaceEntity> | undefined,
    entityType?: string,
  ) {
    if (!InterfaceHelper.isOfflineModeEnabled(interfaceRecord)) return false;
    if (!entityType) return true;
    return InterfaceHelper.offlineModeSyncEntityTypes(interfaceRecord).includes(entityType);
  }

  static offlineModeSyncEntities(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    const syncEntities = interfaceRecord?.properties?.offlineMode?.syncEntities;

    return syncEntities?.filter(
      (syncEntity) => !SYNC_ENTITIES_TO_FILTER.has(syncEntity.entityType as UAEntityTypes),
    );
  }

  static enableCustomIconSync(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return Boolean(
      interfaceRecord?.properties?.offlineMode?.syncEntities?.some(
        (syncEntity) => syncEntity.entityType === UAEntityTypes.CustomIcon,
      ),
    );
  }

  static offlineModeSyncEntityTypes(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return _compact(
      InterfaceHelper.offlineModeSyncEntities(interfaceRecord)?.map(
        (syncEntity) => syncEntity.entityType,
      ),
    );
  }

  static offlineModeSyncEntityConfig(
    interfaceRecord: Partial<InterfaceEntity> | undefined,
    entityType: string,
  ) {
    return InterfaceHelper.offlineModeSyncEntities(interfaceRecord)?.find(
      (syncEntity) => syncEntity.entityType === entityType,
    )?.config;
  }

  static isStandardApplication(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return interfaceRecord?.properties?.standard === true;
  }

  static isProvisionApplication(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return interfaceRecord?.properties?.provisionApp === true;
  }

  static pagePermissions(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return interfaceRecord?.properties?.pagePermissions;
  }

  static shouldShowApplicationURL(interfaceRecord?: InterfaceEntity, interfaceBaseDomain?: string) {
    const isStandardApplication = InterfaceHelper.isStandardApplication(interfaceRecord);
    const isTemplateInterface = InterfaceHelper.isTemplateInterface(interfaceRecord);
    const isProvisionApplication = InterfaceHelper.isProvisionApplication(interfaceRecord);

    // we want to show application url for non standard applications and provision applications (e.g. agentic ai applications)
    return (
      Boolean(interfaceBaseDomain) &&
      (!isStandardApplication || isProvisionApplication) &&
      !isTemplateInterface
    );
  }

  static getGlobalInterfacePageId(interfaceId: string) {
    return `${GLOBAL_INTERFACE_PAGE_ID_PREFIX}${interfaceId}`;
  }

  static getCommonNamespaceId(interfaceId: string, locale: string) {
    return `${COMMON_NAMESPACE_ID_PREFIX}${interfaceId}_${locale}`;
  }

  static getGlobalInterfacePageSlug(interfaceId: string) {
    return `${GLOBAL_INTERFACE_PAGE_SLUG_PREFIX}${interfaceId}`;
  }

  static isGlobalInterfacePageId(pageId: string | undefined) {
    return pageId?.startsWith(GLOBAL_INTERFACE_PAGE_ID_PREFIX);
  }

  static getGlobalInterfacePageComponent(interfaceId: string): InterfaceComponent & { id: string } {
    return {
      slug: InterfaceHelper.getGlobalInterfacePageSlug(interfaceId),
      type: InterfacePageEntityComponentType.PAGE,
      id: InterfaceHelper.getGlobalInterfacePageId(interfaceId),
    };
  }

  static getGlobalInterfacePageWalkthroughComponent(interfaceId: string): InterfaceComponent {
    return {
      type: InterfacePageEntityComponentType.WALKTHROUGH,
      id: InterfaceHelper.getGlobalInterfacePageId(interfaceId),
    };
  }

  static isGlobalInterfacePageSupported(interfaceRecord: InterfaceEntity | undefined) {
    return (
      InterfaceHelper.isApplicationType(interfaceRecord) &&
      !InterfaceHelper.isTemplateInterface(interfaceRecord)
    );
  }

  static getLocalStorageKey(interfaceId: string) {
    return `ua_interface_${interfaceId}`;
  }

  static getSessionStorageKey(interfaceId: string) {
    return `__ua_session_storage_${interfaceId}`;
  }

  static getInterfaceBasePath(interfaceRecord: InterfaceEntity | undefined) {
    const interfaceProperties = interfaceRecord?.properties;
    const basePath = interfaceProperties?.basePath || interfaceProperties?.navigation?.basePath;

    if (basePath) {
      return basePath.startsWith('/') ? basePath : `/${basePath}`;
    }

    return undefined;
  }

  static getPageTransition(interfaceRecord: InterfaceEntity | undefined) {
    return interfaceRecord?.properties?.navigation?.pageTransition;
  }

  static getPublicPageIds(interfaceRecord?: InterfaceEntity): string[] {
    // get public page ids from both the old source (publicPageIds)
    const publicPageIds = interfaceRecord?.properties?.publicPageIds ?? (EMPTY_ARRAY as string[]);

    // the new source (entityDetailsMap)
    const entityDetailsPublicPageIds =
      InterfaceHelper.getEntityDetailsPublicPageIds(interfaceRecord);

    // and merge them, removing duplicates. if there are duplicates,
    // the page will be considered public (which is the safest option)
    const mergedSet = new Set<string>([...entityDetailsPublicPageIds, ...publicPageIds]);
    return Array.from(mergedSet);
  }

  static getEntityDetailsPublicPageIds(interfaceRecord?: InterfaceEntity): string[] {
    // get public page ids from entityDetailsMap where type is PAGE and isPublic is true
    const entityDetailsMap = InterfaceHelper.getEntityDetailsMap(interfaceRecord);
    const result: string[] = [];
    for (const [pageId, details] of Object.entries(entityDetailsMap)) {
      if (details?.type === InterfacePageEntityComponentType.PAGE && details?.isPublic) {
        result.push(pageId);
      }
    }
    return result;
  }

  static getInterfacePageIdVsSlugMap(interfaceRecord: InterfaceEntity | undefined) {
    const entityDetailsMapPageIdVsSlugMap =
      InterfaceHelper.getEntityDetailsMapPageIdVsSlugMap(interfaceRecord);
    const existingInterfacePageIdVsSlugMap =
      interfaceRecord?.properties?.interfacePageIdVsSlugMap ??
      (EMPTY_OBJECT as Record<string, string | undefined>);

    // if both the maps have the same pageId, entityDetailsMapPageIdVsSlugMap[pageId] will override existingInterfacePageIdVsSlugMap[pageId]
    const mergedPageIdVsSlugMap: Record<string, string | undefined> | undefined = {
      ...existingInterfacePageIdVsSlugMap,
      ...entityDetailsMapPageIdVsSlugMap,
    };
    return mergedPageIdVsSlugMap ?? (EMPTY_OBJECT as Record<string, string | undefined>);
  }

  static getInterfaceSlugVsPageIdMap(interfaceRecord: InterfaceEntity | undefined) {
    const pageIdVsSlugMap = InterfaceHelper.getInterfacePageIdVsSlugMap(interfaceRecord);
    return reverseRecord(pageIdVsSlugMap) as Record<string, string | undefined>;
  }

  static getEntityDetailsType(entityDetials: EntityDetails) {
    if (entityDetials.type) {
      return entityDetials.type;
    }
    return InterfacePageEntityComponentType.MODULE;
  }

  static getInterfaceNavigationModuleIdVsSlugMap(interfaceRecord: InterfaceEntity | undefined) {
    // get module-id-vs-slug-map of the entities from entityDetailsMap where type is MODULE
    const entityDetailsMap = InterfaceHelper.getEntityDetailsMap(interfaceRecord);
    const result: Record<string, string | undefined> = {};
    for (const [moduleId, value] of Object.entries(entityDetailsMap)) {
      if (
        value !== undefined &&
        InterfaceHelper.getEntityDetailsType(value) === InterfacePageEntityComponentType.MODULE
      ) {
        result[moduleId] = value.slug;
      }
    }
    return result;
  }

  static getInterfacePageSlugFromPageId(
    interfaceRecord: InterfaceEntity | undefined,
    targetPageId: string,
  ) {
    const entityDetailsMap = interfaceRecord?.properties.entityDetailsMap;
    const interfacePageIdVsSlugMap = interfaceRecord?.properties.interfacePageIdVsSlugMap;
    return entityDetailsMap?.[targetPageId]?.slug ?? interfacePageIdVsSlugMap?.[targetPageId];
  }

  static getInterfacePaths(interfaceRecord: InterfaceEntity | undefined) {
    return interfaceRecord?.properties?.paths;
  }

  static isPageTransitionEnabled(interfaceRecord: InterfaceEntity | undefined) {
    return interfaceRecord?.properties?.navigation?.pageTransition !== undefined;
  }

  static isMqttDisabled(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return interfaceRecord?.properties?.disableMqtt ?? false;
  }

  static getAppHosting(interfaceRecord: InterfaceEntity | undefined, appHosting: AppHostingType) {
    const basePath = InterfaceHelper.getInterfaceBasePath(interfaceRecord);
    return basePath ? 'PATH_BASED' : appHosting;
  }

  static getInterfaceDomain(interfaceId: string, baseDomain: string, appHosting: AppHostingType) {
    if (appHosting === 'PATH_BASED') {
      return `${baseDomain}${interfaceId}`;
    }
    return `${interfaceId}${baseDomain}`;
  }

  static getBasePathForCreateInterface(
    interfaceId: string | undefined,
    basePathPrefix: string,
    appHosting: AppHostingType,
  ) {
    if (appHosting === 'PATH_BASED') {
      return `${basePathPrefix}${interfaceId}`;
    }
    return undefined;
  }

  static getAppVariants(interfaceRecord: InterfaceEntity | undefined) {
    return interfaceRecord?.properties.appVariants;
  }

  static isPublic(
    interfaceRecord: InterfaceEntity | undefined,
    parentInterfaceRecord?: InterfaceEntity,
  ) {
    return interfaceRecord?.properties?.parentInterfaceId
      ? parentInterfaceRecord?.properties.security?.type === SecurityType.Public
      : interfaceRecord?.properties.security?.type === SecurityType.Public;
  }

  static getEntityDetailsMap(interfaceRecord: InterfaceEntity | undefined) {
    return (
      interfaceRecord?.properties?.entityDetailsMap ??
      (EMPTY_OBJECT as Record<string, EntityDetails>)
    );
  }

  static getEntityDetailsMapPageIdVsSlugMap(interfaceRecord: InterfaceEntity | undefined) {
    const entityDetailsMap = InterfaceHelper.getEntityDetailsMap(interfaceRecord);
    const result: Record<string, string | undefined> = {};
    for (const [pageId, value] of Object.entries(entityDetailsMap)) {
      if (value !== undefined && value.type === InterfacePageEntityComponentType.PAGE) {
        result[pageId] = value.slug;
      }
    }
    return result;
  }

  static getBrandAssets = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.manifest?.brandAssets;
  };

  static resolvePageIdFromPath(
    path: string,
    slug: string,
    interfaceRecord?: InterfaceEntity,
  ): string | undefined {
    const normalizedPath = getNormalizedPathFromSlugOrPathNames(path);
    const normalizedSlug = getNormalizedPathFromSlugOrPathNames(slug);
    const pageParam = normalizedPath ? normalizedPath : normalizedSlug;

    return resolvePageFromPathForPathAndSlug({
      pageParam,
      pathNodes: InterfaceHelper.getInterfacePaths(interfaceRecord),
      pageIdToSlugMap: InterfaceHelper.getInterfacePageIdVsSlugMap(interfaceRecord),
    }).pageId;
  }

  static resolvePageFromSegments(
    pageParam: string,
    interfaceRecord?: InterfaceEntity,
  ): ResolveResult {
    return resolvePageFromPathForPathAndSlug({
      pageParam,
      pathNodes: InterfaceHelper.getInterfacePaths(interfaceRecord),
      pageIdToSlugMap: InterfaceHelper.getInterfacePageIdVsSlugMap(interfaceRecord),
    });
  }

  static getAllInterfacePageFullRawPaths(params: {
    pathNodes?: Record<string, InterfacePathNode>;
    pageIdToSlugMap?: Record<string, string | undefined>;
  }) {
    return getAllInterfacePageFullRawPaths(params);
  }

  static getFullRawNonNormalizedPathByPageId(
    pageId: string,
    interfaceRecord?: InterfaceEntity,
  ): string | undefined {
    return getFullRawNonNormalizedPathByPageId({
      pageId,
      pathNodes: InterfaceHelper.getInterfacePaths(interfaceRecord),
      pageIdToSlugMap: InterfaceHelper.getInterfacePageIdVsSlugMap(
        interfaceRecord as InterfaceEntity | undefined,
      ),
    });
  }

  static getRawNonNormalizedPathOrSlugForPage(
    pageId: string,
    interfaceRecord: InterfaceEntity | undefined,
    interfacePage?: InterfacePageEntity,
  ) {
    return (
      InterfaceHelper.getFullRawNonNormalizedPathByPageId(pageId, interfaceRecord) ||
      interfacePage?.properties.slug ||
      pageId
    );
  }

  static getBuilderValuesByParamNameForPage(
    pageId: string,
    interfaceRecord: InterfaceEntity | undefined,
  ): Record<string, string | undefined> {
    return getBuilderValueByParamNameForPageId({
      pageId,
      pathNodes: InterfaceHelper.getInterfacePaths(interfaceRecord),
      pageIdToSlugMap: InterfaceHelper.getInterfacePageIdVsSlugMap(interfaceRecord),
    });
  }

  static getPageRouteDetails(
    pageId: string | undefined,
    interfaceRecord: InterfaceEntity | undefined,
  ) {
    const pathOrSlug = pageId
      ? InterfaceHelper.getRawNonNormalizedPathOrSlugForPage(pageId, interfaceRecord)
      : '';
    const { slug } = getPageSlugAndPath(pathOrSlug);
    return { slug, path: pathOrSlug };
  }
}

export default InterfaceHelper;

// todo automatic filder pages in automation nodes
